import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import { Identifier, useDataProvider } from 'react-admin';

import type { CrmDataProvider } from '@/providers/types';
import { LeadSourcePreviewDialog } from './LeadSourcePreviewDialog';
import { leadSourceKind, leadSourceLabel } from './leadSourceUtils';

function LeadSourceThumb({
    leadId,
    sourcePath,
    onOpen,
}: {
    leadId: Identifier;
    sourcePath: string;
    onOpen: () => void;
}) {
    const kind = leadSourceKind(sourcePath);
    const isImage = kind === 'image';
    const dataProvider = useDataProvider<CrmDataProvider>();
    const [src, setSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(isImage);

    useEffect(() => {
        if (!isImage) {
            setSrc(null);
            setLoading(false);
            return undefined;
        }
        let objectUrl: string | null = null;
        let cancelled = false;
        setLoading(true);
        setSrc(null);
        dataProvider.getLeadSource(leadId, sourcePath)
            .then(({ blob }: { blob: Blob }) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            })
            .catch(() => {
                if (!cancelled) setSrc(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [leadId, sourcePath, isImage, dataProvider]);

    const label = leadSourceLabel(sourcePath);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
                overflow: 'hidden',
                '&:hover': { borderColor: 'primary.main' },
            }}
        >
            <Box
                component="button"
                type="button"
                aria-label={`Preview ${label}`}
                onClick={onOpen}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    border: 0,
                    bgcolor: 'transparent',
                    p: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    flex: 1,
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        height: 96,
                        bgcolor: 'grey.100',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    {isImage && loading ? (
                        <CircularProgress size={22} />
                    ) : isImage && src ? (
                        <Box
                            component="img"
                            src={src}
                            alt=""
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                        />
                    ) : kind === 'audio' ? (
                        <AudioFileIcon
                            sx={{ fontSize: 48, color: 'text.secondary' }}
                            aria-hidden
                        />
                    ) : isImage ? (
                        <ImageIcon
                            sx={{ fontSize: 48, color: 'text.secondary' }}
                            aria-hidden
                        />
                    ) : (
                        <InsertDriveFileIcon
                            sx={{ fontSize: 48, color: 'text.secondary' }}
                            aria-hidden
                        />
                    )}
                </Box>
                <Typography
                    variant="caption"
                    sx={{
                        px: 1,
                        py: 0.75,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </Typography>
            </Box>
        </Box>
    );
}

export function LeadSourceGallery({
    leadId,
    sources,
}: {
    leadId: Identifier;
    sources: string[];
}) {
    const [selectedPath, setSelectedPath] = useState<string | null>(null);

    if (!sources.length) return null;

    return (
        <>
            <Box
                display="grid"
                gap={1.5}
                sx={{
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                }}
            >
                {sources.map(path => (
                    <LeadSourceThumb
                        key={path}
                        leadId={leadId}
                        sourcePath={path}
                        onOpen={() => setSelectedPath(path)}
                    />
                ))}
            </Box>
            <LeadSourcePreviewDialog
                leadId={leadId}
                sourcePath={selectedPath}
                onClose={() => setSelectedPath(null)}
            />
        </>
    );
}
